/** @format */

/**
 * Google Analytics Service
 * Comprehensive tracking for user actions, page views, and events
 * Works alongside Azure Application Insights and HIPAA Audit Logging
 */

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

const GA_MEASUREMENT_ID = 'G-96KRWJBM00';
const GA_DEBUG_MODE = process.env.NODE_ENV === 'development';

/**
 * Check if Google Analytics is loaded and available
 */
const isGAAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Initialize Google Analytics
 * This is called automatically when the script loads, but we can configure it further here
 */
export const initializeGA = (): void => {
  if (!isGAAvailable()) {
    console.warn('Google Analytics: gtag not available yet');
    return;
  }

  try {
    // Get user information for consistent tracking
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        // Set user ID for cross-session tracking (anonymized for privacy)
        if (user.user_id) {
          window.gtag!('config', GA_MEASUREMENT_ID, {
            user_id: `user_${user.user_id}`,
            send_page_view: false,
          });
        }
      } catch (error) {
        console.error('GA: Error parsing user data:', error);
      }
    }

    if (GA_DEBUG_MODE) {
      console.log('✅ Google Analytics initialized:', GA_MEASUREMENT_ID);
    }
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track page view
 */
export const trackPageView = (
  pagePath: string,
  pageTitle?: string,
  additionalParams?: Record<string, any>
): void => {
  if (!isGAAvailable()) {
    if (GA_DEBUG_MODE) console.warn('GA: Not available for page view');
    return;
  }

  try {
    const params: Record<string, any> = {
      page_path: pagePath,
      page_title: pageTitle || document.title,
      ...additionalParams,
    };

    // Add user context if available
    const userStr = localStorage.getItem('user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.user_preferred_plan) {
          params.user_plan = user.user_preferred_plan.plan_name;
        }
      } catch (error) {
        console.error('GA: Error adding user context:', error);
      }
    }

    window.gtag!('event', 'page_view', params);

    if (GA_DEBUG_MODE) {
      console.log('📄 GA Page View:', pagePath, params);
    }
  } catch (error) {
    console.error('GA: Failed to track page view:', error);
  }
};

/**
 * Track custom event
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
): void => {
  if (!isGAAvailable()) {
    if (GA_DEBUG_MODE) console.warn('GA: Not available for event');
    return;
  }

  try {
    window.gtag!('event', eventName, eventParams);

    if (GA_DEBUG_MODE) {
      console.log('📊 GA Event:', eventName, eventParams);
    }
  } catch (error) {
    console.error('GA: Failed to track event:', error);
  }
};

/**
 * Track user login
 */
export const trackLogin = (method: string): void => {
  trackEvent('login', {
    method, // 'Email', 'Google', 'Microsoft'
  });
};

/**
 * Track user signup
 */
export const trackSignup = (method: string): void => {
  trackEvent('sign_up', {
    method,
  });
};

/**
 * Track search
 */
export const trackSearch = (searchTerm: string, resultsCount?: number): void => {
  trackEvent('search', {
    search_term: searchTerm,
    results_count: resultsCount,
  });
};

/**
 * Track button/link click
 */
export const trackClick = (
  elementName: string,
  elementType?: string,
  additionalParams?: Record<string, any>
): void => {
  trackEvent('click', {
    element_name: elementName,
    element_type: elementType || 'button',
    ...additionalParams,
  });
};

/**
 * Track form submission
 */
export const trackFormSubmit = (
  formName: string,
  success: boolean,
  errorMessage?: string
): void => {
  trackEvent('form_submit', {
    form_name: formName,
    success,
    error_message: errorMessage,
  });
};

/**
 * Track file download
 */
export const trackFileDownload = (
  fileName: string,
  fileType?: string,
  fileSize?: number
): void => {
  trackEvent('file_download', {
    file_name: fileName,
    file_type: fileType,
    file_size: fileSize,
  });
};

/**
 * Track video play
 */
export const trackVideoPlay = (videoTitle: string, videoDuration?: number): void => {
  trackEvent('video_play', {
    video_title: videoTitle,
    video_duration: videoDuration,
  });
};

/**
 * Track feature usage
 */
export const trackFeatureUse = (
  featureName: string,
  details?: Record<string, any>
): void => {
  trackEvent('feature_use', {
    feature_name: featureName,
    ...details,
  });
};

/**
 * Track error
 */
export const trackError = (
  errorMessage: string,
  errorContext?: string,
  fatal?: boolean
): void => {
  trackEvent('exception', {
    description: errorMessage,
    context: errorContext,
    fatal: fatal || false,
  });
};

/**
 * Track timing (e.g., page load time, API response time)
 */
export const trackTiming = (
  name: string,
  value: number,
  category?: string,
  label?: string
): void => {
  trackEvent('timing_complete', {
    name,
    value,
    event_category: category || 'timing',
    event_label: label,
  });
};

/**
 * Track user engagement (scroll depth, time on page, etc.)
 */
export const trackEngagement = (
  engagementType: string,
  value: number | string,
  details?: Record<string, any>
): void => {
  trackEvent('engagement', {
    engagement_type: engagementType,
    value,
    ...details,
  });
};

/**
 * Track conversion/goal completion
 */
export const trackConversion = (
  conversionName: string,
  value?: number,
  currency?: string
): void => {
  trackEvent('conversion', {
    conversion_name: conversionName,
    value,
    currency: currency || 'USD',
  });
};

/**
 * Set user properties (for segmentation)
 */
export const setUserProperties = (properties: Record<string, any>): void => {
  if (!isGAAvailable()) {
    if (GA_DEBUG_MODE) console.warn('GA: Not available for user properties');
    return;
  }

  try {
    window.gtag!('set', 'user_properties', properties);

    if (GA_DEBUG_MODE) {
      console.log('👤 GA User Properties:', properties);
    }
  } catch (error) {
    console.error('GA: Failed to set user properties:', error);
  }
};

/**
 * Track e-commerce: Begin checkout
 */
export const trackBeginCheckout = (items: any[], value?: number): void => {
  trackEvent('begin_checkout', {
    items,
    value,
    currency: 'USD',
  });
};

/**
 * Track e-commerce: Purchase
 */
export const trackPurchase = (
  transactionId: string,
  value: number,
  items?: any[],
  currency?: string
): void => {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency: currency || 'USD',
    items,
  });
};

/**
 * Track e-commerce: Refund
 */
export const trackRefund = (
  transactionId: string,
  value: number,
  currency?: string
): void => {
  trackEvent('refund', {
    transaction_id: transactionId,
    value,
    currency: currency || 'USD',
  });
};

/**
 * Set custom dimension (for additional tracking)
 * In GA4, custom dimensions are set as user properties
 */
export const setCustomDimension = (
  dimensionName: string,
  value: string
): void => {
  if (!isGAAvailable()) {
    if (GA_DEBUG_MODE) console.warn('GA: Not available for custom dimension');
    return;
  }

  try {
    // GA4 uses user_properties for custom dimensions
    window.gtag!('set', 'user_properties', {
      [dimensionName]: value
    });

    if (GA_DEBUG_MODE) {
      console.log('🏷️ GA Custom Dimension:', dimensionName, value);
    }
  } catch (error) {
    console.error('GA: Failed to set custom dimension:', error);
  }
};

/**
 * Opt user out of tracking (GDPR/privacy compliance)
 */
export const optOut = (): void => {
  if (typeof window !== 'undefined') {
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    if (GA_DEBUG_MODE) {
      console.log('🚫 GA: User opted out of tracking');
    }
  }
};

/**
 * Opt user back in to tracking
 */
export const optIn = (): void => {
  if (typeof window !== 'undefined') {
    (window as any)[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    if (GA_DEBUG_MODE) {
      console.log('✅ GA: User opted in to tracking');
    }
  }
};

const googleAnalyticsService = {
  initializeGA,
  trackPageView,
  trackEvent,
  trackLogin,
  trackSignup,
  trackSearch,
  trackClick,
  trackFormSubmit,
  trackFileDownload,
  trackVideoPlay,
  trackFeatureUse,
  trackError,
  trackTiming,
  trackEngagement,
  trackConversion,
  setUserProperties,
  trackBeginCheckout,
  trackPurchase,
  trackRefund,
  setCustomDimension,
  optOut,
  optIn,
};

export default googleAnalyticsService;
